import { TeamOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FilterValue } from 'antd/lib/table/interface'
import moment from 'moment'
import TruncatedText from '../../components/truncatedText/TruncatedText'
import { IVolym3Brukare, IVolym3Manad, IVolym3Svar } from '../../gen/api'
import '../../styles/flagLink.css'
import { IYearMonthObject } from '../arendeFordelning/components/tables/tableConfig'
import { celltext, enDecimal, jamforProcent, kortManad, procent } from './format'
import { IdagTooltip, ManadTooltip } from './tooltips'

// Kolumnerna i Volymuppföljning (pejlen-produkt#107): en rad per brukare (A6), Beställd tid (A9, A10)
// och en kolumn per månad med "utförd tid (utförandegrad)" (A21). Kolumnen Beslut finns inte längre.
// Bredderna är minsta bredd, som i flaggtabellerna: tabellen är minst summan av dem och delar ut
// resten av skärmen på kolumnerna. Namnet är låst till vänster när man scrollar i sidled.

// Svaret från /volume2. Används bara av den gamla koden, som tas bort i städkortet när alla tre
// kommunerna är godkända (A25).
export interface IVolumeData {
  beräknad_omfattning_månad: number
  beslutsid: number
  brukare_namn: string
  brukare_personnummer: string
  från: string
  till: string
  data: Array<IYearMonthObject>
}

const TOOLTIP_STIL = { maxWidth: 600 }

// Bredderna rymmer rubrik och sorteringspil på en rad. Innevarande månad är bredare för "(pågår)".
const BREDD_NAMN = 190
const BREDD_PERSONNUMMER = 140
const BREDD_BESTALLD = 115
const BREDD_MANAD = 110
const BREDD_MANAD_PAGAR = 150

// Innehållet centreras vertikalt i cellen, även när grannkolumnerna gör raden högre
const mitten = () => ({ style: { verticalAlign: 'middle' as const } })

// Rubriker och celler bryter aldrig rad
const enRad = (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text}</span>

/** Tabellens minsta bredd: summan av de kolumner som visas, månaderna under gruppen inräknade */
export const tabellbredd = (kolumner: ColumnsType<IVolym3Brukare>): number =>
  kolumner.reduce((summa, k) => summa + ('children' in k && k.children ? tabellbredd(k.children) : (k.width as number) || 0), 0)

/** Månaderna i perioden som ÅÅÅÅ-MM */
export const manaderIPerioden = (fran: string, till: string): string[] => {
  const manader: string[] = []
  const m = moment(fran, 'YYYY-MM-DD').startOf('month')
  const slut = moment(till, 'YYYY-MM-DD').startOf('month')
  while (!m.isAfter(slut)) {
    manader.push(m.format('YYYY-MM'))
    m.add(1, 'month')
  }
  return manader
}

const manad = (b: IVolym3Brukare, ym: string): IVolym3Manad | undefined => b.manader.find((m) => m.ym === ym)

const procentFor = (b: IVolym3Brukare, ym: string) => {
  const m = manad(b, ym)
  return m ? procent(m) : null
}

export const skapaKolumner = (svar: IVolym3Svar, filters: Record<string, FilterValue | null>): ColumnsType<IVolym3Brukare> => {
  const namn = Array.from(new Set(svar.brukare.map((b) => b.namn))).sort((a, b) => a.localeCompare(b))
  const innevarande = moment(svar.idag, 'YYYY-MM-DD').format('YYYY-MM')

  const kolumner: ColumnsType<IVolym3Brukare> = [
    {
      title: enRad('Namn'),
      key: 'namn',
      width: BREDD_NAMN,
      fixed: 'left',
      onCell: mitten,
      filteredValue: filters.namn || null,
      filters: namn.map((n) => ({ value: n, text: n })),
      filterSearch: true,
      onFilter: (value, record) => record.namn === value,
      sorter: (a, b) => a.namn.localeCompare(b.namn),
      showSorterTooltip: { title: 'Sortera på namn' },
      // Länken går till brukarens sida, som i Mina ärenden, även när namnet visas som Sekretess. Personnumret
      // står då i sekretessformat. Bara en brukare utan personnummer får ingen länk.
      render: (_, b) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <TruncatedText text={b.namn} to={b.personnummer ? { pathname: `/app/brukare/id=${b.personnummer}&namn=${b.namn}` } : undefined} />
          </span>
          {b.andraHandlaggare.length > 0 && (
            <Tooltip title={`Brukaren har pågående hemtjänst i ärenden hos flera handläggare: ${b.andraHandlaggare.join(', ')}`} overlayClassName="flag-tooltip">
              <TeamOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: enRad('Personnummer'),
      key: 'personnummer',
      width: BREDD_PERSONNUMMER,
      onCell: mitten,
      sorter: (a, b) => (a.personnummer || '').localeCompare(b.personnummer || ''),
      showSorterTooltip: { title: 'Sortera på personnummer' },
      render: (_, b) => (b.personnummer ? <TruncatedText text={b.personnummer} /> : '–'),
    },
    {
      title: <Tooltip title="Omfattning av nuvarande beställning - antal beställda timmar hemtjänst per månad.">{enRad('Beställd tid')}</Tooltip>,
      key: 'bestalldIdag',
      width: BREDD_BESTALLD,
      onCell: mitten,
      sorter: (a, b, sortOrder) => jamforProcent(a.bestalldIdag, b.bestalldIdag, sortOrder),
      showSorterTooltip: false,
      render: (_, b) => (
        <Tooltip title={<IdagTooltip brukare={b} idag={svar.idag} />} overlayStyle={TOOLTIP_STIL}>
          <span>{b.bestalldIdag === null ? '–' : enDecimal(b.bestalldIdag)}</span>
        </Tooltip>
      ),
    },
  ]

  const manadskolumner: ColumnsType<IVolym3Brukare> = manaderIPerioden(svar.fran, svar.till).map((ym) => {
    const pagar = ym === innevarande
    return {
      title: enRad(`${kortManad(ym)}${pagar ? ' (pågår)' : ''}`),
      key: ym,
      width: pagar ? BREDD_MANAD_PAGAR : BREDD_MANAD,
      onCell: mitten,
      sorter: (a: IVolym3Brukare, b: IVolym3Brukare, sortOrder?: 'ascend' | 'descend' | null) => jamforProcent(procentFor(a, ym), procentFor(b, ym), sortOrder),
      showSorterTooltip: { title: 'Sortera på utförandegrad' },
      render: (_: unknown, b: IVolym3Brukare) => {
        const m = manad(b, ym)
        const text = celltext(m)
        if (!m || text === '–') return <span>–</span>
        return (
          <Tooltip title={<ManadTooltip manad={m} senasteBesok={svar.senasteBesok} />} overlayStyle={TOOLTIP_STIL}>
            <span style={{ whiteSpace: 'nowrap', fontStyle: pagar ? 'italic' : undefined }}>{text}</span>
          </Tooltip>
        )
      },
    }
  })

  kolumner.push({
    title: enRad('Utförd tid och utförandegrad'),
    key: 'manader',
    children: manadskolumner,
  })

  return kolumner
}
